import ViktorClient from '@/api/clients/viktorClient/ViktorClient';
/* CONSTS */
import { DEFAULT_PER_PAGE } from '@/api/constants';
import { A_PLAYER_SCORE_MINIMUM_VALUE } from '@/enums/constants';
import { encodeAfURIParams } from '@/helpers/uri';
import type { PaginatedResponse } from '@/types/api/axios';
/* TYPES */
import type { CampaignUsersResponse, PostCampaignParams, PostCampaignResponse } from '@/types/api/campaign';
import type { CampaignDataWithSync, PostExtractParams } from '@/types/api/recruiter/recruiterCampaigns';
import type { BatchCall, BatchCallData, BatchResponse } from '@/types/batches/batches.types';
import type { StepStatusId } from '@/types/campaigns/campaignSteps.types';

/* API */
import { getRecruiterContactsBatchQuery, postBatch } from '../batches/batches.api';
import type { ContactsQueryParams } from '../contacts/contacts.types';
import type { CampaignStepUser, CampaignUsersQueryParams } from './campaign.types';

/* GET */
/**
 * Get the details of a given campaign
 * @param {string} uuid - uuid of campaign
 * @returns {Promise<CampaignDataWithSync>} - promise with data on the requested campaign
 */
export function getCampaign({ uuid }): Promise<CampaignDataWithSync> {
  const params = {
    fields: 'location',
    extra_data: ['pm_details', 'syncing', 'steps', 'step_actions', 'nb_total_users']
  };

  return ViktorClient().get(`/campaign/${uuid}`, { params });
}

/**
 * Get the users of a campaign
 * @param data
 * @param {string} campaign.uuid - uuid of campaign
 * @param {number} campaign.perPage - number of campaigns to fetch per page
 * @param {number} campaign.page - page to fetch
 */
export function getCampaignUsers({ uuid, perPage, page = 1 }): Promise<CampaignUsersResponse> {
  const params = {
    per_page: perPage || DEFAULT_PER_PAGE,
    page
  };
  return ViktorClient().get(`/campaign/${uuid}/users`, { params });
}

export function getCampaignSteps({ campaignUuid }) {
  const params = {
    extra_data: 'nb_users'
  };

  return ViktorClient().get(`/campaign/${campaignUuid}/steps`, { params });
}

// Format campaign query used for query batches in global mode
export function getStepsUsersBatchQuery(
  campaignUuid,
  params: Partial<CampaignUsersQueryParams> & { step_uuid: string }
): BatchCall {
  const apiParams: Partial<CampaignUsersQueryParams> & { campaign_uuid: string; step_uuid: string } = {
    ...params,
    campaign_uuid: campaignUuid
  };
  delete apiParams.page;
  delete apiParams.per_page;
  delete apiParams.order;

  return {
    endpoint: `/viktor/campaign/:campaign_uuid/step/:step_uuid/users`,
    method: 'GET',
    data: [{ params: encodeAfURIParams(apiParams) }]
  };
}

export async function getStepsUsers({
  campaignUuid,
  stepUuid,
  statusId,
  sorting,
  page,
  searchTerm,
  uuid,
  candidateAplayerScore,
  is_shape_done,
  is_drive_done,
  is_brain_done,
  is_voice_done,
  is_screening_done,
  rating,
  adequacy_score,
  adequacy_score_max,
  brain,
  shape_dimensions,
  drive_dimensions,
  voice_uuids,
  screening_statuses,
  screening_criteria_uuid
}: {
  campaignUuid: string;
  stepUuid: string;
  statusId?: StepStatusId;
  sorting?: string;
  page?: number;
  searchTerm?: string;
  uuid?: string;
  rating?: number[];
  candidateAplayerScore?: boolean;
  is_shape_done?: boolean;
  is_drive_done?: boolean;
  is_brain_done?: boolean;
  is_voice_done?: boolean;
  is_screening_done?: boolean;
  adequacy_score?: number;
  adequacy_score_max?: number;
  brain?: boolean;
  shape_dimensions?: number[];
  drive_dimensions?: number[];
  voice_uuids?: string[];
  screening_statuses?: string[];
  screening_criteria_uuid?: string[];
}): Promise<PaginatedResponse<CampaignStepUser[]> & { params: CampaignUsersQueryParams }> {
  const params: Partial<CampaignUsersQueryParams> = {
    page,
    per_page: 15,
    status_id: statusId || undefined,
    order: sorting || undefined,
    'search[user_data:slug_full_name,user:email]': searchTerm?.trim() || undefined,
    'user:uuid': uuid,
    rating: rating?.length ? rating : undefined,
    is_shape_done,
    is_drive_done,
    is_brain_done,
    is_voice_done,
    is_screening_done,
    aplayer_score: candidateAplayerScore ? A_PLAYER_SCORE_MINIMUM_VALUE : undefined,
    adequacy_score,
    adequacy_score_max,
    brain,
    shape_dimensions,
    drive_dimensions,
    voice_uuids,
    screening_status: screening_statuses?.length ? screening_statuses : undefined,
    screening_criteria_uuid: screening_criteria_uuid || undefined,
    fields: 'user_data.location'
  };
  const response = await ViktorClient().get(`/campaign/${campaignUuid}/step/${stepUuid}/users`, { params });
  return { ...response, params }; // Adding request params to response
}

/* PATCH */
/**
 *
 * @param {string} uuid uuid of campaign to update
 * @param {Partial<PostCampaignParams>} params updated params
 * @returns {Promise<void>}
 */
export function patchCampaign(uuid, params: Partial<PostCampaignParams>) {
  return ViktorClient().patch(`/campaign/${uuid}`, params);
}

/* POST */

/**
 * Create a campaign
 * @param {PostCampaignParams} data - params to apply
 * @returns {Promise<PostCampaignResponse>} - promise with data on created campaign
 */

export function postCampaign(campaign: PostCampaignParams): Promise<PostCampaignResponse> {
  return ViktorClient().post('/campaign', campaign);
}

/**
 * Add groups in a campaign
 * @param {string} uuid uuid of campaign to add the users in
 * @param {string[]} groupUuids array of uuids of groups to add
 */

export async function postCampaignGroups(uuid, groupUuids): Promise<BatchResponse> {
  const basePath = `/campaign/${uuid}/group/`;
  // If only one group, do a single API call, else use the bulk
  if (groupUuids.length === 1) {
    return ViktorClient().post(basePath + groupUuids[0]);
  } else {
    const promises: Promise<any>[] = [];
    groupUuids.forEach(groupUuid => {
      promises.push(ViktorClient().post(basePath + groupUuid));
    });
    const response = (await Promise.allSettled(promises)) as any;
    return response.map(response => (response.status === 'rejected' ? response.reason : response.value));
  }
}

/**
 * Add users in a campaign
 * @param {string[]} campaignUuids uuids of campaign to add the users in
 * @param {string[]} emails array of emails to add
 * @param globalParams - global params for batch if global mode
 */

export async function postCampaignUsers(
  campaignUuids,
  emails,
  globalParams: ContactsQueryParams | undefined = undefined
): Promise<BatchResponse> {
  // GLOBAL MODE - if multiple campaigns, one batch per campaign
  if (!emails.length && globalParams) {
    const promises: Promise<any>[] = [];
    campaignUuids.forEach(uuid => {
      const data = [{ params: { campaign_uuid: uuid } }];
      promises.push(
        postBatch({
          query: getRecruiterContactsBatchQuery(globalParams),
          action: { method: 'POST', endpoint: '/viktor/campaign/:campaign_uuid/user', data }
        })
      );
    });

    const response = (await Promise.allSettled(promises)) as any;
    return response.map(response => (response.status === 'rejected' ? response.reason : response.value));
  } else if (emails.length === 1 && campaignUuids.length === 1) {
    // SINGLE MODE
    const params = {
      email: emails[0]
    };
    return ViktorClient().post(`/campaign/${campaignUuids[0]}/user`, params);
  } else {
    // BULK MODE
    const data: BatchCallData[] = [];
    emails.forEach(email => {
      data.push(...campaignUuids.map(uuid => ({ params: { campaign_uuid: uuid }, body: { email } })));
    });
    return postBatch({
      action: { method: 'POST', endpoint: '/viktor/campaign/:campaign_uuid/user', data }
    });
  }
}

export function postCampaignStep({ campaignUuid, categoryId, label, order }) {
  const params = {
    category_id: categoryId,
    label,
    order
  };

  return ViktorClient().post(`/campaign/${campaignUuid}/step`, params);
}

/**
 * Get the extract data for a given campaign @TODO TYPE
 */
export function postCampaignExtract({ uuid, data }: { uuid: string; data: PostExtractParams }): Promise<any> {
  return ViktorClient().post(`/campaign/${uuid}/extract`, data, { responseType: 'blob' });
}

/* DELETE */

/**
 * Delete a campaign
 * @param {string} uuid - campaign uud to delete
 */

export function deleteCampaign(uuid) {
  return ViktorClient().delete(`/campaign/${uuid}`);
}
